import React, { useState, useEffect, useRef } from 'react';
import { legalContent, parseContent, extractToc } from '../legalContent';

const docTabs = [
  { key: 'privacy', path: '/privacy-policy', ro: 'Confidențialitate', en: 'Privacy Policy' },
  { key: 'terms',   path: '/terms',           ro: 'Termeni și Condiții', en: 'Terms & Conditions' },
  { key: 'cookies', path: '/cookies',         ro: 'Cookie-uri', en: 'Cookie Policy' },
  { key: 'dpa',     path: '/dpa',              ro: 'DPA', en: 'DPA' },
];

export const LegalPage = ({ type, onNavigate }) => {
  const [lang, setLang] = useState('ro');
  const [activeId, setActiveId] = useState('');
  const contentRef = useRef(null);

  const doc = legalContent[type][lang];
  const toc = extractToc(doc.content);

  useEffect(() => { window.scrollTo(0, 0); }, [type, lang]);

  // Highlight active TOC item on scroll
  useEffect(() => {
    const onScroll = () => {
      if (!contentRef.current) return;
      const headings = contentRef.current.querySelectorAll('h2[id], h3[id]');
      let current = '';
      headings.forEach((h) => {
        if (h.getBoundingClientRect().top < 120) current = h.id;
      });
      setActiveId(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [doc]);

  const handleTabClick = (e, tab) => {
    e.preventDefault();
    window.history.pushState({}, '', tab.path);
    onNavigate(tab.path.replace('/', ''));
  };

  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const offset = 100;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="legal-page">

      {/* ── Page header ──────────────────────────────── */}
      <div className="legal-page-header">
        <div className="legal-page-header-inner">

          <div className="legal-page-topbar">
            <a href="/" className="legal-breadcrumb" onClick={(e) => { e.preventDefault(); onNavigate('home'); }}>
              ← {lang === 'ro' ? 'Acasă' : 'Home'}
            </a>
            <div className="lang-toggle">
              <button
                className={`lang-btn${lang === 'ro' ? ' active' : ''}`}
                onClick={() => setLang('ro')}
              >RO</button>
              <button
                className={`lang-btn${lang === 'en' ? ' active' : ''}`}
                onClick={() => setLang('en')}
              >EN</button>
            </div>
          </div>

          <nav className="legal-page-tabs" role="tablist">
            {docTabs.map((tab) => (
              <a
                key={tab.key}
                href={tab.path}
                role="tab"
                aria-selected={type === tab.key}
                className={`legal-page-tab${type === tab.key ? ' active' : ''}`}
                onClick={(e) => handleTabClick(e, tab)}
              >
                {lang === 'ro' ? tab.ro : tab.en}
              </a>
            ))}
          </nav>

        </div>
      </div>

      {/* ── Two-column layout ─────────────────────────── */}
      <div className="legal-page-layout">

        {/* TOC sidebar */}
        <aside className="legal-toc" aria-label="Table of contents">
          <p className="legal-toc-label">{lang === 'ro' ? 'Cuprins' : 'Contents'}</p>
          <nav>
            <ul className="legal-toc-list">
              {toc.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className={`legal-toc-link${item.level === 3 ? ' sub' : ''}${activeId === item.id ? ' active' : ''}`}
                    onClick={(e) => handleTocClick(e, item.id)}
                  >
                    {item.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Main content */}
        <article className="legal-main" ref={contentRef}>
          <header className="legal-doc-header">
            <h1 className="legal-title">{doc.title}</h1>
            <p className="legal-updated">
              {lang === 'ro' ? 'Ultima actualizare:' : 'Last updated:'} {doc.lastUpdated}
            </p>
          </header>
          <div className="legal-content">
            {parseContent(doc.content)}
          </div>
          <footer className="legal-doc-footer">
            <p>© {new Date().getFullYear()} Poly Tool (Poly Tool Design S.R.L.){lang === 'ro' ? ' Toate drepturile rezervate.' : ' All rights reserved.'}</p>
          </footer>
        </article>

      </div>
    </div>
  );
};

export default LegalPage;
